import { createAction, createApiAction } from '@saucelabs/sl-util-redux';
import Sentry from '@saucelabs/sl-util-sentry';

import {
  fetchRecurlyToken,
  getAccount,
  getLocation,
  updateAccount,
  updateBillingInfo,
} from './api';
import {
  CARD_DETAILS_SAVED,
  CARD_VALID_SAVED,
  FETCH_ACCOUNT,
  FETCH_BILLING_LOCATION,
  FETCH_RECURLY_TOKEN,
  PAYMENT_INFO_SAVED,
  PAYMENT_METHOD_CHANGED,
  RECURLY_COMPONENT_FETCH,
  RECURLY_COMPONENT_READY,
  SET_RECURLY_INSTANCE,
  UPDATE_ACCOUNT,
  UPDATE_BILLING_INFO,
} from './types';
import { getAccountParams } from './utils';

export const paymentComponentFetchAction = createAction(RECURLY_COMPONENT_FETCH);
export const paymentComponentReadyAction = createAction(RECURLY_COMPONENT_READY);
export const paymentInfoSavedAction = createAction(PAYMENT_INFO_SAVED);
export const cardDetailsSavedAction = createAction(CARD_DETAILS_SAVED);
export const cardDetailsValidAction = createAction(CARD_VALID_SAVED);
export const setRecurlyInstance = createAction(SET_RECURLY_INSTANCE);

export const fetchRecurlyTokenAction = createApiAction(
  FETCH_RECURLY_TOKEN,
  (form) => fetchRecurlyToken(form),
);

export const paymentMethodChangedAction = (cardType, lastFour, isValid) => ({
  type: PAYMENT_METHOD_CHANGED,
  payload: { cardType, lastFour, isValid },
});

export const retrieveAccountAction = createApiAction(
  FETCH_ACCOUNT,
  () => getAccount(),
  { meta: { skipErrorPage: true } },
);

export const getBillingLocation = createApiAction(
  FETCH_BILLING_LOCATION,
  () => getLocation(),
);

const stripBillingInfo = (billingInfo) => ({
  billingInfo: {
    emailPresent: !!billingInfo?.billing_info?.email,
    firstNamePresent: !!billingInfo?.billing_info?.first_name,
    lastNamePresent: !!billingInfo?.billing_info?.last_name,
    countryPresent: !!billingInfo?.billing_info?.address?.country,
    postalCodePresent: !!billingInfo?.billing_info?.address?.postal_code,
  },
  emailPresent: !!billingInfo?.email,
  firstNamePresent: !!billingInfo?.first_name,
  lastNamePresent: !!billingInfo?.last_name,
  postalCodePresent: !!billingInfo?.address?.postal_code,
  threeDsTokenId: billingInfo?.three_d_secure_action_result_token_id,
  tokenIdPresent: !!billingInfo?.token_id,
});

export const updateBillingInfoAction = createApiAction(
  UPDATE_BILLING_INFO,
  ({ token, values, threeDsTokenId }) => {
    const accountParams = getAccountParams(token, values, threeDsTokenId);
    return updateBillingInfo(accountParams.billing_info)
      .catch((error) => {
        if (!['3d_secure_action_required', 'three_d_secure_action_required'].includes(error?.response?.data?.detail?.category)) { // Ignoring 3DSecure2 verification "errors"
          Sentry.withScope((scope) => {
            scope.setContext('billingInfo', stripBillingInfo(accountParams.billing_info));
            scope.setContext('responseData', JSON.stringify(error?.response?.data));
            Sentry.captureException(new Error('Failed to update billing account information'));
          });
        }
        throw error;
      });
  },
);

export const updateAccountAction = createApiAction(
  UPDATE_ACCOUNT,
  ({ token, values, threeDsTokenId }) => {
    const accountParams = getAccountParams(token, values, threeDsTokenId);

    return updateAccount(accountParams)
      .catch((error) => {
        if (!['3d_secure_action_required', 'three_d_secure_action_required'].includes(error?.response?.data?.detail?.category)) { // Ignoring 3DSecure2 verification "errors"
          Sentry.withScope((scope) => {
            scope.setContext('billingInfo', stripBillingInfo(accountParams.billing_info));
            scope.setContext('responseData', JSON.stringify(error?.response?.data));
            Sentry.captureException(new Error('Failed to update billing info'));
          });
        }
        throw error;
      });
  },
);
